import $ from 'jquery';
import 'jquery-ui/ui/widgets/autocomplete';

export default class SearchAutocompleteModule {
  /**
   * @param {HTMLElement} element - The .wp-block-search element
   * @param {Object} options
   */
  constructor (element, options = {}) {
    this.element = element;
    this.$element = $(element); // wrapper element
    this.$input = this.$element.find('.wp-block-search__input');

    this.opts = {
      apiBase: DGFM?.apiBase || '/wp-json/', // now uses DGFM.apiBase
      perPage: options.perPage ?? 8,
      minChars: options.minChars ?? 2,
      subtypes: Array.isArray(options.subtypes) ? options.subtypes : [],
      seeAllText: options.seeAllText || 'Alle Ergebnisse anzeigen',
    };

    this._boundOnResponse = this._onResponse.bind(this);
    this._destroyed = false;

    this.init();
  }

  // -------- convenient static init ---------
  static initAll (selector = '.wp-block-search', options = {}) {
    document.querySelectorAll(selector).forEach((el) => {
      if (!el._dgfmSearchAC) el._dgfmSearchAC = new SearchAutocompleteModule(el, options);
    });
  }

  init () {
    if (!this.$input.length || this._destroyed) return;

    this.$input
      .autocomplete({
        minLength: this.opts.minChars,
        delay: 150,
        appendTo: this.$element,
        source: (request, response) => {
          const url = this._buildSearchURL(request.term);
          fetch(url, { credentials: 'same-origin' })
            .then((res) => (res.ok ? res.json() : []))
            .then((items) =>
              response(
                items.map((item) => ({
                  label: this._stripHTML(item.title),
                  value: this._stripHTML(item.title),
                  url: item.url,
                })),
              ),
            )
            .catch(() => response([]));
        },
        select: (e, ui) => {
          if (ui?.item?.url) window.location.href = ui.item.url;
        },
        open: () => this.$input.attr('aria-expanded', 'true'),
        close: () => this.$input.attr('aria-expanded', 'false'),
      })
      .on('autocompleteresponse', this._boundOnResponse);

    // Enter opens active item
    this.$input.on('keydown.dgfm', (e) => {
      if (e.key === 'Enter') {
        const $menu = this.$input.autocomplete('widget');
        const $active = $menu.find('.dgfm-ac-active');
        if ($active.length) {
          const url = $active.data('url');
          if (url) {
            e.preventDefault();
            window.location.href = url;
          }
        }
      }
    });

    // Custom renderer without subtype
    this.$input.autocomplete('instance')._renderItem = (ul, item) => {
      return $('<li>')
        .addClass('dgfm-ac-li')
        .data('url', item.url)
        .append(
          $('<div class="dgfm-ac-item">').text(item.label),
        )
        .appendTo(ul);
    };

    // Track active item
    const $menu = this.$input.autocomplete('widget');
    $menu.on('menufocus.dgfm', (event, ui) => {
      $menu.find('.dgfm-ac-active').removeClass('dgfm-ac-active');
      ui.item.addClass('dgfm-ac-active');
    });
    $menu.on('menublur.dgfm', () => {
      $menu.find('.dgfm-ac-active').removeClass('dgfm-ac-active');
    });
  }

  destroy () {
    if (this._destroyed) return;
    try {
      this.$input.autocomplete('destroy');
    } catch (_) {}
    this.$input.off('autocompleteresponse', this._boundOnResponse);
    this.$input.off('keydown.dgfm'); // NEW: remove key handler
    // NEW: remove menu listeners
    try { this.$input.autocomplete('widget').off('.dgfm'); } catch (_) {}
    this.$element.find('.dgfm-ac-footer').remove();
    this._destroyed = true;
  }

  _stripHTML (html) {
    return $('<div>').html(html).text();
  }

  _buildSearchURL (term) {
    const base = this.opts.apiBase.replace(/\/+$/, '') + '/wp/v2/search';
    const url = new URL(base, window.location.origin);
    url.searchParams.set('search', term);
    url.searchParams.set('per_page', String(this.opts.perPage));
    url.searchParams.set('_fields', 'title,url,subtype');
    if (this.opts.subtypes.length) {
      this.opts.subtypes.forEach((st) => url.searchParams.append('subtype', st));
    }
    return url.toString();
  }

  _onResponse () {
    const term = this.$input.val().trim();
    if (term.length < this.opts.minChars) return;

    const $menu = this.$input.autocomplete('widget');
    $menu.find('.dgfm-ac-footer').remove();

    const formAction =
      this.$element.find('form').attr('action') ||
      window.location.origin + window.location.pathname;

    const searchURL = new URL(formAction, window.location.origin);
    searchURL.searchParams.set('s', term);

    $('<li class="dgfm-ac-footer">')
      .append(
        $('<a class="dgfm-ac-seeall" href="#">')
          .text(this.opts.seeAllText)
          .on('click', (ev) => {
            ev.preventDefault();
            window.location.href = searchURL.toString();
          }),
      )
      .appendTo($menu);
  }
}